import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { findMatchingAction, ErrorAction, WorkOrderContext } from '@/lib/errorActions';

interface ActiveCorrection {
  action: ErrorAction;
  workOrder: WorkOrderContext;
  retryFn?: () => Promise<void>;
}

export function useSmartError() {
  const { toast } = useToast();
  const [activeCorrection, setActiveCorrection] = useState<ActiveCorrection | null>(null);

  const handleError = useCallback((
    error: any,
    workOrder: WorkOrderContext,
    retryFn?: () => Promise<void>
  ) => {
    const errorMessage = error?.message || error?.code || String(error);
    const action = findMatchingAction(errorMessage);

    if (action) {
      toast({
        title: action.title,
        description: action.description,
        action: (
          <button
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
            onClick={() => setActiveCorrection({ action, workOrder, retryFn })}
          >
            {action.actionLabel}
          </button>
        ),
      });
      return true;
    }

    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
    });
    return false;
  }, [toast]);

  const clearCorrection = useCallback(() => {
    setActiveCorrection(null);
  }, []);

  const onCorrectionSuccess = useCallback(async () => {
    const retryFn = activeCorrection?.retryFn;
    setActiveCorrection(null);
    if (retryFn) {
      await retryFn();
    }
  }, [activeCorrection]);

  return {
    handleError,
    activeCorrection,
    clearCorrection,
    onCorrectionSuccess,
    CorrectionDialog: activeCorrection ? activeCorrection.action.component : null,
  };
}
